import { useQueryClient } from "@tanstack/react-query";
import { createContext, type ReactNode, useCallback, useContext, useMemo } from "react";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import {
	contestCriteriaKeys,
	useCriteria,
	useSections,
} from "../../services/contest-criteria/queries";
import { getCurrentContestId } from "../../services/contests/utils";
import type { Criterion, Section } from "../../types";

interface ItemsState<T> {
	items: T[];
	setItems: (items: T[] | ((prev: T[]) => T[])) => void;
	loading: boolean;
	setLoading: () => void;
}

interface ContestCriteriaContextValue {
	sections: ItemsState<Section>;
	criteria: ItemsState<Criterion>;
	loading: boolean;
}

const defaultContextValue: ContestCriteriaContextValue = {
	sections: {
		items: [],
		setItems: () => {},
		loading: false,
		setLoading: () => {},
	},
	criteria: {
		items: [],
		setItems: () => {},
		loading: false,
		setLoading: () => {},
	},
	loading: false,
};

const ContestCriteriaContext = createContext<ContestCriteriaContextValue>(defaultContextValue);

export const useContestCriteriaContext = () => useContext(ContestCriteriaContext);

interface ContestCriteriaProviderProps {
	children: ReactNode | ((context: ContestCriteriaContextValue) => ReactNode);
}

export const ContestCriteriaProvider = ({ children }: ContestCriteriaProviderProps) => {
	const queryClient = useQueryClient();
	const contestId = getCurrentContestId();

	const { data: sections = [], isLoading: sectionsLoading } = useSections();
	const { data: criteria = [], isLoading: criteriaLoading } = useCriteria();

	// These setItems functions update the query cache directly for optimistic updates
	const setSections = useCallback(
		(newSections: Section[] | ((prev: Section[]) => Section[])) => {
			queryClient.setQueryData(
				contestCriteriaKeys.sectionsList(contestId),
				(prev: Section[] | undefined) =>
					typeof newSections === "function" ? newSections(prev ?? []) : newSections,
			);
		},
		[queryClient, contestId],
	);

	const setCriteria = useCallback(
		(newCriteria: Criterion[] | ((prev: Criterion[]) => Criterion[])) => {
			queryClient.setQueryData(
				contestCriteriaKeys.criteriaList(contestId),
				(prev: Criterion[] | undefined) =>
					typeof newCriteria === "function" ? newCriteria(prev ?? []) : newCriteria,
			);
		},
		[queryClient, contestId],
	);

	const contextValue = useMemo<ContestCriteriaContextValue>(
		() => ({
			sections: {
				items: sections,
				setItems: setSections,
				loading: sectionsLoading,
				setLoading: () => {},
			},
			criteria: {
				items: criteria,
				setItems: setCriteria,
				loading: criteriaLoading,
				setLoading: () => {},
			},
			loading: sectionsLoading || criteriaLoading,
		}),
		[sections, criteria, sectionsLoading, criteriaLoading, setSections, setCriteria],
	);

	return (
		<ContestCriteriaContext.Provider value={contextValue}>
			<div className="relative">
				{contextValue.loading && (
					<div className="absolute inset-0 z-10 flex items-center justify-center bg-background/50">
						<Spinner size="lg" />
					</div>
				)}
				<div className={cn(contextValue.loading && "opacity-50 pointer-events-none")}>
					{typeof children === "function" ? children(contextValue) : children}
				</div>
			</div>
		</ContestCriteriaContext.Provider>
	);
};
